import { kindLabel, relationTitle } from "../../lib/architecture";

const ROW = 54;
const WIDTH = 860;
const BOX = 250;
const MAX_SHOWN = 12;
const cut = (text, n) => (text.length > n ? `${text.slice(0, n - 1)}…` : text);

// The element at the centre, what points to it on the left, what it points to on the right. Solid line = read in the code, dashed = deduced.
// Every box is a link reachable by keyboard; the same information is also given as a list below (never the graph alone).
export default function RelationGraph({ graph, onOpen }) {
  const side = (items) => items.slice(0, MAX_SHOWN);
  const incoming = side(graph.in);
  const outgoing = side(graph.out);
  const rows = Math.max(incoming.length, outgoing.length, 1);
  const height = rows * ROW + 24;
  const mid = height / 2;
  const y = (index, count) => 12 + (index + 0.5) * ((height - 24) / count);
  const more = (list, hidden) => list.length - MAX_SHOWN + hidden;
  const hiddenIn = Math.max(0, more(graph.in, graph.hidden.in));
  const hiddenOut = Math.max(0, more(graph.out, graph.hidden.out));

  const neighbour = (relation, index, count, left) => {
    const cy = y(index, count);
    const x = left ? 0 : WIDTH - BOX;
    const startX = left ? BOX : WIDTH - BOX;
    const endX = left ? WIDTH / 2 - BOX / 2 : WIDTH / 2 + BOX / 2;
    const bend = (startX + endX) / 2;
    const dash = relation.confidence === "static" ? undefined : "5 4";
    const open = () => onOpen(relation.other.id);
    return (
      <g key={`${relation.direction}-${relation.kind}-${relation.other.id}`}>
        <path d={`M ${startX} ${cy} C ${bend} ${cy}, ${bend} ${mid}, ${endX} ${mid}`}
          fill="none" className="stroke-slate-400" strokeWidth="1.25" strokeDasharray={dash} />
        <g role="link" tabIndex={0} aria-label={`${relationTitle(relation.kind, relation.direction)} ${relation.other.name}, ${kindLabel(relation.other.kind)}`}
          data-testid="graph-neighbour" onClick={open} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
          className="cursor-pointer outline-none [&:focus-visible>rect]:stroke-jamora [&:focus-visible>rect]:stroke-2">
          <title>{`${relation.other.name} — ${kindLabel(relation.other.kind)}`}</title>
          <rect x={x} y={cy - 23} width={BOX} height={46} rx="6" className="fill-white stroke-slate-300 hover:fill-slate-50" />
          <text x={x + 10} y={cy - 7} className="fill-slate-500" fontSize="12">{cut(`${relationTitle(relation.kind, relation.direction)} · ${kindLabel(relation.other.kind)}`, 34)}</text>
          <text x={x + 10} y={cy + 12} className="fill-slate-900" fontSize="14" fontWeight="500">{cut(relation.other.name, 28)}</text>
        </g>
      </g>
    );
  };

  return (
    <div data-testid="relation-graph">
      <svg viewBox={`0 0 ${WIDTH} ${height}`} role="group" aria-label={`Schéma des relations de ${graph.center.name}`} className="h-auto w-full">
        {incoming.map((r, i) => neighbour(r, i, incoming.length, true))}
        {outgoing.map((r, i) => neighbour(r, i, outgoing.length, false))}
        <g>
          <rect x={WIDTH / 2 - BOX / 2} y={mid - 30} width={BOX} height={60} rx="8" className="fill-teal-50 stroke-teal-700" strokeWidth="1.5" />
          <text x={WIDTH / 2} y={mid - 8} textAnchor="middle" className="fill-teal-800" fontSize="12">{kindLabel(graph.center.kind)}</text>
          <text x={WIDTH / 2} y={mid + 14} textAnchor="middle" className="fill-slate-900" fontSize="15" fontWeight="600">{cut(graph.center.name, 26)}</text>
        </g>
        {incoming.length === 0 ? <text x="10" y={mid} className="fill-slate-400" fontSize="12">Rien ne pointe ici</text> : null}
        {outgoing.length === 0 ? <text x={WIDTH - 10} y={mid} textAnchor="end" className="fill-slate-400" fontSize="12">Ne pointe vers rien</text> : null}
      </svg>
      {hiddenIn || hiddenOut ? (
        <p className="mt-1 text-xs text-slate-500" data-testid="graph-hidden">
          {hiddenIn ? `${hiddenIn} relation(s) entrante(s) non dessinée(s)` : ""}{hiddenIn && hiddenOut ? " · " : ""}{hiddenOut ? `${hiddenOut} relation(s) sortante(s) non dessinée(s)` : ""}.
        </p>
      ) : null}
      <p className="mt-1 text-2xs text-slate-500">Trait plein : lu dans le code. Trait pointillé : déduit. Le schéma laisse de côté les imports et les définitions de fichiers ; la liste ci-dessous donne toutes les relations.</p>
    </div>
  );
}
